import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { Injectable, inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { MILLISECONDS_IN_A_DAY, TimelineTimeframe } from '@wilson/interfaces';
import { WilsonState } from '@wilson/non-domain-specific/decorators/wilson-state';
import {
  SetVisibleDateRange,
  ShiftTimelineDataState,
  ShiftTimelineSettingsState,
  timelineDaysByZoomLevel,
  TimelineSetDate,
} from '@wilson/shift-timeline/state';
import { addDays, isValid, subDays } from 'date-fns';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  filter,
  map,
  Observable,
  Subscription,
} from 'rxjs';
import { NUMBER_OF_DAYS_TO_LOAD } from '../shift-timeline-date-picker/constants';
import { getSupposedVisibleInterval } from './supposed-visible-interval.fn';

@Injectable()
export class HorizontalScrollingManagerService {
  private store = inject(Store);

  @WilsonState<number>(-1)
  private previousLeftScrollOffsetPx!: number;
  private previousLeftScrollOffsetPx$!: Observable<number>;

  public readonly timelineAssignmentRegionWidthPxUpdateSubject =
    new BehaviorSubject<Date>(new Date());

  private scrollDirection: 'left' | 'right' | 'none' = 'none';
  private ignoreScrollEvent = false;

  resetScrollTrackingData(): void {
    this.scrollDirection = 'none';
    this.previousLeftScrollOffsetPx = -1;
  }

  updateScrollTrackingData({
    leftOffsetPx,
    rightOffsetPx,
  }: {
    leftOffsetPx: number;
    rightOffsetPx: number;
  }): void {
    /**
     * For some reason when scrolling all the way to the right/left, the scroll event is triggered twice with the same offset values,
     * which leads to wrong assumption that nothing has changed.
     * To fix this, we check if the offset values are less than 1px, then we immediately flag it as moving left/right
     */
    if (leftOffsetPx > this.previousLeftScrollOffsetPx || rightOffsetPx < 1) {
      this.scrollDirection = 'right';
    } else if (
      leftOffsetPx < this.previousLeftScrollOffsetPx ||
      leftOffsetPx < 1
    ) {
      this.scrollDirection = 'left';
    } else if (leftOffsetPx === this.previousLeftScrollOffsetPx) {
      this.scrollDirection = 'none';
    }
    this.previousLeftScrollOffsetPx = leftOffsetPx;
  }

  subscribeToDateRangeUpdater(
    rowContainerRef$: Observable<CdkVirtualScrollViewport | undefined>,
  ): Subscription {
    const debounceTimeForLoadMoreDays = 100;

    return combineLatest([
      rowContainerRef$.pipe(filter((viewport) => !!viewport)),
      this.previousLeftScrollOffsetPx$,
      this.store.select(ShiftTimelineDataState.timeframe),
      this.store.select(ShiftTimelineSettingsState.zoomLevel),
      this.store.select(ShiftTimelineSettingsState.pixelPerMs()),
    ])
      .pipe(
        debounceTime(debounceTimeForLoadMoreDays),
        filter(
          ([
            viewport,
            previousLeftScrollOffsetPx,
            timeframe,
            zoomLevel,
            pixelPerMs,
          ]) =>
            viewport !== undefined &&
            previousLeftScrollOffsetPx !== undefined &&
            timeframe !== undefined &&
            zoomLevel !== undefined &&
            pixelPerMs !== undefined,
        ),
        debounceTime(10),
        map(
          ([
            viewport,
            previousLeftScrollOffsetPx,
            timeframe,
            zoomLevel,
            pixelPerMs,
          ]) => {
            const supposedVisibleInterval = getSupposedVisibleInterval({
              timeframe,
              pixelPerMs,
              scrollLeftOffsetPx: previousLeftScrollOffsetPx,
              numberOfDaysInVisibleInterval: timelineDaysByZoomLevel.get(
                zoomLevel,
              ) as number,
            });

            return { supposedVisibleInterval, viewport };
          },
        ),
      )
      .subscribe(({ supposedVisibleInterval, viewport }) => {
        const isIntervalValid =
          isValid(supposedVisibleInterval.start) &&
          isValid(supposedVisibleInterval.end);
        const previousSupposedVisibleInterval = this.store.selectSnapshot(
          ShiftTimelineDataState.visibleDateRange,
        );
        const isSameInterval =
          previousSupposedVisibleInterval &&
          previousSupposedVisibleInterval.start &&
          previousSupposedVisibleInterval.start.toString() ===
            supposedVisibleInterval.start.toString() &&
          previousSupposedVisibleInterval.end &&
          previousSupposedVisibleInterval.end.toString() ===
            supposedVisibleInterval.end.toString();

        if (isIntervalValid && !isSameInterval) {
          this.store.dispatch(
            new SetVisibleDateRange(
              supposedVisibleInterval as TimelineTimeframe,
            ),
          );
        }

        if (!this.ignoreScrollEvent && !isSameInterval) {
          if (isIntervalValid) {
            this.loadDataForMoreDays(
              supposedVisibleInterval,
              viewport as CdkVirtualScrollViewport,
            );
          }
        } else {
          this.ignoreScrollEvent = false;
        }
      });
  }

  public loadDataForMoreDaysToLeft(viewport: CdkVirtualScrollViewport): void {
    const timeframe = this.store.selectSnapshot(
      ShiftTimelineDataState.timeframe,
    );

    const newStartDate = subDays(timeframe.start, NUMBER_OF_DAYS_TO_LOAD);
    const newTimeframe = {
      start: newStartDate.getTime(),
      end: timeframe.end,
    };
    const scrollLeftOffsetPx =
      this.store.selectSnapshot(ShiftTimelineSettingsState.pixelPerMs()) *
      NUMBER_OF_DAYS_TO_LOAD *
      MILLISECONDS_IN_A_DAY;

    this.store.dispatch([new TimelineSetDate(newTimeframe)]);

    this.previousLeftScrollOffsetPx = scrollLeftOffsetPx;

    /**
     * This flag is necessary to block the next handling of the scroll event listener so that we don't have an infinite loop
     */
    this.ignoreScrollEvent = true;
    viewport.scrollTo({
      left: scrollLeftOffsetPx,
    });
  }

  private loadDataForMoreDays(
    currentVisibleDateRange: Interval,
    viewport: CdkVirtualScrollViewport,
  ): void {
    const dayWidthPx = this.store.selectSnapshot(
      ShiftTimelineSettingsState.pixelWidthPerDay(),
    );
    const numberOfDaysToLoad = NUMBER_OF_DAYS_TO_LOAD;

    if (currentVisibleDateRange.start && currentVisibleDateRange.end) {
      const rightScrollOffsetPxToLoadMoreData =
        (dayWidthPx * numberOfDaysToLoad) / 3;
      const leftScrollOffsetToLoadMoreData =
        (dayWidthPx * numberOfDaysToLoad) / 3;

      if (
        this.scrollDirection === 'right' &&
        viewport.measureScrollOffset('right') <=
          rightScrollOffsetPxToLoadMoreData
      ) {
        const timeframe = this.store.selectSnapshot(
          ShiftTimelineDataState.timeframe,
        );
        const startDate = timeframe.start;
        const endDate = addDays(timeframe.end, numberOfDaysToLoad);

        this.store.dispatch([
          new TimelineSetDate({
            start: startDate,
            end: endDate,
          }),
        ]);
      } else if (
        this.scrollDirection === 'left' &&
        viewport.measureScrollOffset('left') <= leftScrollOffsetToLoadMoreData
      ) {
        this.loadDataForMoreDaysToLeft(viewport);
      }
    }
  }
}
